import { gsap } from 'gsap';

/**
 * MenuAnimations Class
 *
 * Creates smooth GSAP animations for header dropdown menus.
 * Features:
 * - Header height expansion before content appears
 * - Left-to-right column stagger with spring bounce effect
 * - Click-to-toggle functionality with outside-click-to-close
 * - Smooth reverse animations when closing
 */
class MenuAnimations {
    constructor() {
        // DOM element references - these elements must exist in the HTML
        this.headerContainer = document.querySelector('[data-header-container]');  // Main header wrapper
        this.headerContent = document.querySelector('[data-header-content]');      // Inner header content that expands
        this.menus = document.querySelectorAll('.dropdown-menu[data-menu]');       // All dropdown menu containers
        this.menuButtons = document.querySelectorAll('nav [data-menu-button]');    // All menu trigger buttons

        // Animation state tracking
        this.currentTimeline = null;  // Current GSAP timeline instance
        this.isAnimating = false;     // Prevents multiple animations from overlapping
        this.activeMenu = null;       // Currently open menu element

        // Alpine.js integration - find the Alpine component for menuIsOpen state
        this.alpineComponent = null;
        this.findAlpineComponent();

        // Safety check - exit if no menus found
        if (this.menus.length === 0) {
            return;
        }

        // Start the initialization process
        this.init();
    }

    /**
     * Initialize the menu animation system
     * Sets up initial visual states and event handlers
     */
    init() {

        // Set initial states for all menus and their columns
        this.menus.forEach(menu => {
            // Hide all menus initially but keep them in DOM for measurement
            gsap.set(menu, {
                opacity: 0,           // Invisible
                visibility: 'hidden', // Not interactive
                display: 'block'      // But still in document flow for height calculation
            });

            // Find all animatable columns within this menu
            const columns = menu.querySelectorAll('.dropdown-menu-animate');
            if (columns.length > 0) {
                // Convert NodeList to Array for GSAP compatibility
                const columnsArray = Array.from(columns);
                // Set initial state: invisible and 30px above final position
                gsap.set(columnsArray, {
                    opacity: 0,                    // Start invisible
                    y: -30,                       // Start 30px above final position (for top-to-bottom fade)
                    transformOrigin: 'center top' // Scale/rotate from center-top
                });
            }
        });

        // Set up all event handlers
        this.setupClickHandlers();

        // Close menu when clicking outside navigation area
        document.addEventListener('click', (e) => {
            // If click is outside nav and we have an active menu, close it
            if (!e.target.closest('nav') && this.activeMenu) {
                this.closeMenu(this.activeMenu);
            }
        });
    }

    /**
     * Set up click handlers for all menu buttons
     * Uses button text content to determine which menu to open
     */
    setupClickHandlers() {

        // Add click handler to each menu button
        this.menuButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();    // Prevent default button behavior
                e.stopPropagation();  // Stop event from bubbling up

                // Determine which menu this button should open from data attribute
                const menuType = button.getAttribute('data-menu-button');

                if (menuType) {
                    // Find the corresponding menu element
                    const menu = document.querySelector(`[data-menu="${menuType}"]`);
                    if (menu) {
                        // Toggle behavior: if this menu is already open, close it
                        if (this.activeMenu === menu) {
                            this.closeMenu(menu);
                        } else {
                            // Let openMenu handle closing any active menu
                            this.openMenu(menu);
                        }
                    }
                }
            });
        });
    }

    /**
     * Open a menu with smooth GSAP animation
     * Animation sequence:
     * 1. Close any currently active menu first (if different from target)
     * 2. Reset header height to natural state
     * 3. Expand header height to accommodate new menu
     * 4. Fade in menu container
     * 5. Stagger animate columns left-to-right with iOS spring effect
     *
     * @param {HTMLElement} menu - The menu element to open
     */
    openMenu(menu) {

        // Kill any existing animation timeline to prevent conflicts
        if (this.currentTimeline) {
            this.currentTimeline.kill();
        }

        // Update state
        this.isAnimating = true;

        // Calculate the new menu's target height first (before any changes)
        const menuHeight = this.getMenuHeight(menu);              // Get new menu's natural height
        const headerNaturalHeight = this.getHeaderNaturalHeight(); // Get header's natural height without any menu
        const headerTop = this.headerContainer.getBoundingClientRect().top;
        const menuType = menu.getAttribute('data-menu');

        let targetHeight;
        if (menuType === 'mobile-menu') {
            // Mobile menu fills the full viewport
            targetHeight = window.innerHeight - headerTop - 20;
        } else {
            const maxTargetHeight = window.innerHeight - headerTop - 20;
            targetHeight = Math.min(headerNaturalHeight + menuHeight, maxTargetHeight);
        }

        // If there's a currently active menu that's different from the target menu, hide it instantly
        if (this.activeMenu && this.activeMenu !== menu) {
            // Immediately hide the previous menu and reset its columns
            gsap.set(this.activeMenu, {
                opacity: 0,
                visibility: 'hidden'
            });

            const prevColumns = this.activeMenu.querySelectorAll('.dropdown-menu-animate');
            if (prevColumns.length > 0) {
                gsap.set(Array.from(prevColumns), {
                    opacity: 0,
                    y: -30,
                    transformOrigin: 'center top'
                });
            }
        }

        // Update active menu reference
        this.activeMenu = menu;

        // Set dropdown maxHeight before animation starts so content isn't constrained during fade-in
        const availableForMenu = targetHeight - headerNaturalHeight;
        menu.style.maxHeight = Math.max(200, availableForMenu) + 'px';

        // Update Alpine.js state
        this.updateAlpineState(menu.getAttribute('data-menu'), true, true);

        // Create new GSAP timeline for coordinated animations
        this.currentTimeline = gsap.timeline({
            onComplete: () => {
                this.isAnimating = false; // Reset state when animation completes
                this.updateAlpineState(menu.getAttribute('data-menu'), true, false);

                // Recalculate dropdown maxHeight to fit within expanded header
                const headerContentRect = this.headerContent.getBoundingClientRect();
                const menuRect = menu.getBoundingClientRect();
                const availableHeight = headerContentRect.bottom - menuRect.top - 10;
                menu.style.maxHeight = Math.max(200, availableHeight) + 'px';

                // Focus search input if this is the search menu
                if (menu.getAttribute('data-menu') === 'search') {
                    const searchInput = menu.querySelector('input[x-ref="searchInput"]');
                    if (searchInput) {
                        searchInput.focus();
                    }
                }
            }
        });

        // STEP 1: Expand header height smoothly (happens first, before content appears)
        this.currentTimeline.to(this.headerContent, {
            height: targetHeight,     // Expand to calculated height
            duration: 0.4,           // 400ms duration
            ease: "power2.out"       // Smooth easing (fast start, slow end)
        });

        // STEP 2: Show menu container (starts 200ms before header expansion completes)
        this.currentTimeline.to(menu, {
            opacity: 1,              // Fade in
            visibility: 'visible',   // Make interactive
            duration: 0.1           // Quick fade (100ms)
        }, "-=0.2");               // Start 200ms before previous animation ends

        // STEP 3: Animate columns with left-to-right stagger and iOS spring effect
        const columns = menu.querySelectorAll('.dropdown-menu-animate');

        if (columns.length > 0) {
            // Convert NodeList to Array for GSAP compatibility
            const columnsArray = Array.from(columns);
            this.currentTimeline.to(columnsArray, {
                opacity: 1,                  // Fade in
                y: 0,                       // Slide down from -30px to final position (top-to-bottom)
                duration: 0.6,              // 600ms per column
                stagger: 0.1,               // 100ms delay between each column (left-to-right)
                ease: "back.out(1.25)",      // iOS-style spring bounce effect
                transformOrigin: 'center top' // Transform from center-top point
            }, "-=0.1");                   // Start 100ms before previous animation ends
        }
    }

    /**
     * Close a menu with smooth reverse animation
     * Animation sequence:
     * 1. Hide columns right-to-left (reverse stagger)
     * 2. Fade out menu container
     * 3. Contract header height back to original
     *
     * @param {HTMLElement} menu - The menu element to close
     */
    closeMenu(menu) {

        // Kill any existing animation timeline
        if (this.currentTimeline) {
            this.currentTimeline.kill();
        }

        // Update state
        this.isAnimating = true;
        this.activeMenu = null; // Clear active menu reference

        // Update Alpine.js state
        this.updateAlpineState(null, false, true);

        const columns = menu.querySelectorAll('.dropdown-menu-animate');

        // Create new timeline for close animation
        this.currentTimeline = gsap.timeline({
            onComplete: () => {
                this.isAnimating = false;
                this.updateAlpineState(null, false, false);
                // Reset header height to auto after animation completes
                gsap.set(this.headerContent, { height: 'auto' });
                // Reset columns to initial top position for next opening
                if (columns.length > 0) {
                    const columnsArray = Array.from(columns);
                    gsap.set(columnsArray, {
                        opacity: 0,
                        y: -30, // Reset to initial top position
                        transformOrigin: 'center top'
                    });
                }
            }
        });

        // STEP 1: Hide columns with reverse stagger (right-to-left, bottom-to-top)
        if (columns.length > 0) {
            const columnsArray = Array.from(columns);
            this.currentTimeline.to(columnsArray, {
                opacity: 0,              // Fade out
                y: -30,                  // Slide down and out (bottom-to-top exit)
                duration: 0.5,          // Faster than open (300ms)
                stagger: -0.05,         // Negative stagger = reverse order (right-to-left)
                ease: "power2.in"       // Smooth acceleration into fade
            });
        }

        // STEP 2: Hide menu container (starts 100ms before columns finish)
        this.currentTimeline.to(menu, {
            opacity: 0,              // Fade out
            visibility: 'hidden',    // Make non-interactive
            duration: 0.2           // Quick fade (200ms)
        }, "-=0.1");               // Start 100ms before previous ends

        // STEP 3: Contract header height back to original size
        this.currentTimeline.to(this.headerContent, {
            height: 'auto',         // Return to natural height
            duration: 0.3,          // 300ms duration
            ease: "power2.inOut"    // Smooth in and out
        }, "-=0.1");               // Start 100ms before previous ends
    }

    /**
     * Get the natural height of a menu by temporarily showing it off-screen
     * This allows us to measure the menu's content height before animating
     *
     * @param {HTMLElement} menu - The menu element to measure
     * @returns {number} The menu's natural height in pixels
     */
    getMenuHeight(menu) {
        // Remove any maxHeight constraint so we measure true content height
        const originalMaxHeight = menu.style.maxHeight;
        menu.style.maxHeight = 'none';

        // Temporarily show menu off-screen to measure its natural height
        gsap.set(menu, {
            opacity: 0,              // Keep invisible
            visibility: 'visible',   // But make it measurable
            position: 'absolute',    // Take out of normal flow
            top: '-9999px'          // Position way off-screen (left side doesn't matter)
        });

        // Get the actual height now that browser can calculate it
        const height = menu.offsetHeight;

        // Hide it again and return to normal positioning
        gsap.set(menu, {
            opacity: 0,              // Keep invisible
            visibility: 'hidden',    // Make non-interactive again
            position: 'absolute',    // Keep out of flow for now
            top: 'auto'             // Reset positioning
        });

        // Restore original maxHeight
        menu.style.maxHeight = originalMaxHeight;

        return height;
    }

    /**
     * Get the natural height of the header content without any menus
     * This allows us to calculate smooth transitions between different menu heights
     *
     * @returns {number} The header's natural height in pixels
     */
    getHeaderNaturalHeight() {
        // Temporarily hide all menus and reset header height to measure natural height
        const originalMenuStates = [];

        // Store current states and hide all menus
        this.menus.forEach(menu => {
            const currentOpacity = gsap.getProperty(menu, 'opacity');
            const currentVisibility = gsap.getProperty(menu, 'visibility');
            originalMenuStates.push({ menu, currentOpacity, currentVisibility });

            gsap.set(menu, {
                opacity: 0,
                visibility: 'hidden'
            });
        });

        // Temporarily reset header height to auto to get natural height
        const originalHeight = gsap.getProperty(this.headerContent, 'height');
        gsap.set(this.headerContent, { height: 'auto' });

        // Get the natural height
        const naturalHeight = this.headerContent.offsetHeight;

        // Restore original header height
        gsap.set(this.headerContent, { height: originalHeight });

        // Restore original menu states
        originalMenuStates.forEach(({ menu, currentOpacity, currentVisibility }) => {
            gsap.set(menu, {
                opacity: currentOpacity,
                visibility: currentVisibility
            });
        });

        return naturalHeight;
    }

    /**
     * Find the Alpine.js component that contains menuIsOpen state
     * This allows us to sync GSAP animations with Alpine's reactive state
     */
    findAlpineComponent() {
        // The Alpine component is on the body element
        this.alpineComponent = document.body;

        // Wait a bit for Alpine to initialize if needed
        if (!this.alpineComponent._x_dataStack) {
            setTimeout(() => {
                if (!this.alpineComponent._x_dataStack || !this.alpineComponent._x_dataStack[0]) {
                    console.warn('Could not find Alpine.js component with menuIsOpen state');
                }
            }, 100);
        }
    }

    /**
     * Update Alpine.js state to sync with GSAP animations
     * @param {string|null} activeMenu - The currently active menu type
     * @param {boolean} menuIsOpen - Whether any menu is open
     * @param {boolean} isAnimating - Whether animations are currently running
     */
    updateAlpineState(activeMenu, menuIsOpen, isAnimating) {
        if (this.alpineComponent && this.alpineComponent._x_dataStack && this.alpineComponent._x_dataStack[0]) {
            const alpineData = this.alpineComponent._x_dataStack[0];
            alpineData.activeMenu = activeMenu;
            alpineData.menuIsOpen = menuIsOpen;
            alpineData.isAnimating = isAnimating;
        } else {
            console.warn('Cannot update Alpine state - Alpine component not found');
        }
    }

    /**
     * Add method to close active menu (called from Alpine escape handler)
     */
    closeActiveMenu() {
        if (this.activeMenu) {
            this.closeMenu(this.activeMenu);
        }
    }

    /**
     * Cleanup method - kills any running animations
     * Useful for preventing memory leaks if component is destroyed
     */
    destroy() {
        if (this.currentTimeline) {
            this.currentTimeline.kill();
        }
        this.isAnimating = false;
    }
}

// Alpine.js is already initialized in index.js and configured on the body element

// Initialize the menu animations when DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
    window.menuAnimations = new MenuAnimations();
});

export { MenuAnimations };